import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Key, Eye, EyeOff, CheckCircle, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import { sharedStyles, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

/**
 * BYOKSettingsModal - Manage Bring Your Own API Key settings.
 *
 * Allows Pro tier users to provide their own Anthropic API key for unlimited reports.
 *
 * Features:
 * - Toggle to show/hide API key management section (UI-only, not functional enable/disable)
 * - API key input with validation
 * - Real-time validation feedback
 * - Enable BYOK: Validate and save a new key
 * - Disable BYOK: Toggle off and click Save
 * - Rotate key: Click rotate button on existing key
 * - Delete key: Permanently remove key from database
 * - Security warnings and cost transparency
 * - Encrypted storage (backend)
 */
const BYOKSettingsModal = ({ isOpen, onClose }) => {
  const { makeRequest } = useApi();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // BYOK status from backend
  const [byokStatus, setByokStatus] = useState({
    is_pro_tier: false,
    uses_own_api_key: false,
    has_encrypted_key: false,
    monthly_reports_limit: 25,
    byok_available: false
  });

  // Form state
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showApiKeySection, setShowApiKeySection] = useState(false); // UI-only: controls visibility
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch BYOK status on mount
  useEffect(() => {
    if (isOpen) {
      fetchBYOKStatus();
    }
  }, [isOpen]);

  const fetchBYOKStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await makeRequest('users/me/byok/status');
      setByokStatus(data);
      // Initialize toggle based on whether user has BYOK enabled
      setShowApiKeySection(data.uses_own_api_key);
      setApiKey(''); // Don't populate with existing key (security)

      // Reset validation result when opening modal
      // (Don't show "API Key Valid!" box for existing keys - it's redundant)
      setValidationResult(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateKey = async () => {
    if (!apiKey.trim()) {
      setError('Please enter an API key');
      return;
    }

    try {
      setValidating(true);
      setError(null);
      setValidationResult(null);

      const data = await makeRequest('users/me/byok/validate', {
        method: 'POST',
        body: JSON.stringify({ key: apiKey })
      });

      setValidationResult(data);

      if (data.valid) {
        // Clear any previous errors (success shown in validation result box)
        setError(null);
        setSuccess(null);
        // Update monthly_reports_limit preview to show unlimited
        setByokStatus(prev => ({
          ...prev,
          monthly_reports_limit: 999999
        }));
      } else {
        setError(data.error);
        setSuccess(null);
      }
    } catch (err) {
      setError(err.message);
      setValidationResult({ valid: false, error: err.message });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    // Save button ONLY saves a validated API key (if user entered one)
    // It does NOT handle enable/disable toggle logic

    const hasValidatedNewKey = validationResult?.valid && apiKey.trim();

    // User validated a new key → save it and enable BYOK
    if (hasValidatedNewKey) {
      try {
        setLoading(true);
        setError(null);

        const data = await makeRequest('users/me/byok', {
          method: 'PATCH',
          body: JSON.stringify({
            anthropic_key: apiKey,
            enable_byok: true
          })
        });

        setByokStatus(prev => ({
          ...prev,
          uses_own_api_key: data.uses_own_api_key,
          monthly_reports_limit: data.monthly_reports_limit,
          has_encrypted_key: true
        }));

        onClose();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // No new key to save → just close modal
    onClose();
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await makeRequest('users/me/byok', {
        method: 'DELETE'
      });

      setSuccess(data.message);
      setByokStatus(prev => ({
        ...prev,
        uses_own_api_key: false,
        has_encrypted_key: false,
        monthly_reports_limit: 25
      }));
      setShowApiKeySection(false); // Hide API key section after deletion
      setApiKey('');
      setValidationResult(null);
      setShowDeleteConfirm(false);

      // Close modal after brief delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = (checked) => {
    // UI-only: Toggle visibility of API key management section
    setShowApiKeySection(checked);
    setError(null);
    setSuccess(null);

    // If hiding section, clear validation state
    if (!checked) {
      setValidationResult(null);
      setApiKey('');
    }
  };

  if (!isOpen) return null;

  const formStyles = {
    label: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.8rem',
      fontWeight: '500',
      color: sharedStyles.colors.textSecondary,
      display: 'block',
      marginBottom: '0.5rem'
    },
    input: {
      ...sharedStyles.inputStyles,
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem',
      width: '100%',
      border: validationResult?.valid
        ? `2px solid ${sharedStyles.colors.success}`
        : validationResult?.valid === false
          ? `2px solid ${sharedStyles.colors.error}`
          : `2px solid ${sharedStyles.colors.secondaryLight}`
    },
    toggle: {
      position: 'relative',
      display: 'inline-block',
      width: '44px',
      height: '24px'
    },
    toggleInput: {
      opacity: 0,
      width: 0,
      height: 0
    },
    toggleSlider: (checked) => ({
      position: 'absolute',
      cursor: 'pointer',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: checked ? sharedStyles.colors.primary : sharedStyles.colors.textSecondary,
      transition: '0.3s',
      borderRadius: '24px',
      '&:before': {
        position: 'absolute',
        content: '""',
        height: '18px',
        width: '18px',
        left: '3px',
        bottom: '3px',
        backgroundColor: 'white',
        transition: '0.3s',
        borderRadius: '50%',
        transform: checked ? 'translateX(20px)' : 'translateX(0)'
      }
    })
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={onClose}
      >
        {/* Modal */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px',
              borderBottom: `1px solid ${sharedStyles.colors.surfaceAlt}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Key size={24} color={sharedStyles.colors.primary} />
              <h2
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  margin: 0
                }}
              >
                API Settings (BYOK)
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: sharedStyles.colors.textSecondary
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '20px' }}>
            {/* Loading state */}
            {loading && !byokStatus.is_pro_tier && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{
                  fontSize: '0.875rem',
                  color: sharedStyles.colors.textSecondary
                }}>
                  Loading...
                </div>
              </div>
            )}

            {/* Not Pro tier - Upgrade prompt */}
            {!loading && !byokStatus.is_pro_tier && (
              <div
                style={{
                  background: `linear-gradient(135deg, ${sharedStyles.colors.primary}15 0%, ${sharedStyles.colors.primaryDark}15 100%)`,
                  border: `2px solid ${sharedStyles.colors.primary}40`,
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center'
                }}
              >
                <AlertCircle size={48} color={sharedStyles.colors.primary} style={{ marginBottom: '12px' }} />
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  margin: '0 0 8px 0'
                }}>
                  BYOK is a Pro Feature
                </h3>
                <p style={{
                  fontSize: '0.95rem',
                  color: sharedStyles.colors.textSecondary,
                  margin: '0 0 16px 0',
                  lineHeight: '1.5'
                }}>
                  Upgrade to Pro to bring your own Anthropic API key and get unlimited reports.
                </p>
                <button
                  onClick={() => window.location.href = '/#pricing'}
                  style={{
                    ...createButton('primary'),
                    padding: '10px 24px'
                  }}
                >
                  Upgrade to Pro
                </button>
              </div>
            )}

            {/* Pro tier - BYOK settings */}
            {!loading && byokStatus.is_pro_tier && (
              <>
                {/* Status Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div
                    style={{
                      background: sharedStyles.colors.surfaceAlt,
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '8px',
                      padding: '16px'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <span style={{
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: sharedStyles.colors.textPrimary
                      }}>
                        Current Status:
                      </span>
                      <span style={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: byokStatus.uses_own_api_key ? sharedStyles.colors.success : sharedStyles.colors.textSecondary
                      }}>
                        {byokStatus.uses_own_api_key ? '✅ BYOK Enabled' : 'Platform Credits'}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        color: sharedStyles.colors.textPrimary
                      }}>
                        Monthly Reports Limit:
                      </span>
                      <span style={{
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        color: sharedStyles.colors.primary
                      }}>
                        {byokStatus.monthly_reports_limit === 999999 ? '♾️ Unlimited' : byokStatus.monthly_reports_limit}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Toggle API Key Management Section */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0'
                  }}>
                    <div>
                      <label style={{
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        color: sharedStyles.colors.textPrimary,
                        display: 'block',
                        marginBottom: '4px'
                      }}>
                        Manage My Own API Key
                      </label>
                      <span style={{
                        fontSize: '0.8rem',
                        color: sharedStyles.colors.textSecondary
                      }}>
                        Show settings to add, rotate, or delete your Anthropic API key
                      </span>
                    </div>
                    <label style={formStyles.toggle}>
                      <input
                        type="checkbox"
                        checked={showApiKeySection}
                        onChange={(e) => handleToggleVisibility(e.target.checked)}
                        style={formStyles.toggleInput}
                      />
                      <span style={{
                        ...formStyles.toggleSlider(showApiKeySection),
                        display: 'block'
                      }}>
                        <span style={{
                          position: 'absolute',
                          content: '""',
                          height: '18px',
                          width: '18px',
                          left: '3px',
                          bottom: '3px',
                          backgroundColor: 'white',
                          transition: '0.3s',
                          borderRadius: '50%',
                          transform: showApiKeySection ? 'translateX(20px)' : 'translateX(0)'
                        }} />
                      </span>
                    </label>
                  </div>
                </div>

                {/* API Key Management Section (shown when toggle is ON) */}
                {showApiKeySection && (
                  <>
                    {/* STATE 3: User has existing key - show placeholder and rotate option */}
                    {byokStatus.has_encrypted_key ? (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={formStyles.label}>
                          Anthropic API Key
                        </label>
                        <div style={{
                          padding: '12px 16px',
                          background: sharedStyles.colors.successLight,
                          border: `2px solid ${sharedStyles.colors.success}`,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle size={18} color={sharedStyles.colors.success} />
                            <span style={{
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              color: sharedStyles.colors.successDark,
                              fontWeight: '500'
                            }}>
                              sk-ant-••••••••••••••••
                            </span>
                          </div>
                          <span style={{
                            fontSize: '0.75rem',
                            color: sharedStyles.colors.successDark,
                            fontWeight: '500',
                            background: sharedStyles.colors.successLight,
                            padding: '4px 8px',
                            borderRadius: '4px'
                          }}>
                            ✓ Active
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            // Clear validation and switch to input mode
                            setValidationResult(null);
                            setApiKey('');
                            setByokStatus(prev => ({
                              ...prev,
                              has_encrypted_key: false
                            }));
                          }}
                          style={{
                            ...createButton('secondary'),
                            padding: '6px 12px',
                            fontSize: '0.75rem',
                            width: '100%'
                          }}
                        >
                          🔄 Rotate API Key
                        </button>
                        <p style={{
                          fontSize: '0.75rem',
                          color: sharedStyles.colors.textMuted,
                          margin: '6px 0 0 0',
                          textAlign: 'center'
                        }}>
                          Click to replace with a new key
                        </p>
                      </div>
                    ) : (
                      /* STATE 2: User enabled toggle but hasn't submitted key yet */
                      <div style={{ marginBottom: '16px' }}>
                        <label style={formStyles.label}>
                          Anthropic API Key
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={showKey ? 'text' : 'password'}
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder="sk-ant-..."
                            style={formStyles.input}
                            disabled={loading || validating}
                          />
                          <button
                            onClick={() => setShowKey(!showKey)}
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              color: sharedStyles.colors.textSecondary
                            }}
                            disabled={loading || validating}
                          >
                            {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        <p style={{
                          fontSize: '0.75rem',
                          color: sharedStyles.colors.textMuted,
                          margin: '6px 0 0 0'
                        }}>
                          Get your API key from{' '}
                          <a
                            href="https://console.anthropic.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: sharedStyles.colors.primary, textDecoration: 'none' }}
                          >
                            console.anthropic.com
                          </a>
                        </p>
                      </div>
                    )}

                    {/* Validate Button (only show when entering new key) */}
                    {!validationResult?.existing_key && (
                      <div style={{ marginBottom: '24px' }}>
                        <button
                          onClick={handleValidateKey}
                          disabled={loading || validating || !apiKey.trim()}
                          style={{
                            ...createButton('secondary'),
                            padding: '8px 16px',
                            fontSize: '0.875rem',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            opacity: (loading || validating || !apiKey.trim()) ? 0.6 : 1
                          }}
                        >
                          {validating ? (
                            <>
                              <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid currentColor',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite'
                              }} />
                              Validating...
                            </>
                          ) : validationResult?.valid ? (
                            <>
                              <CheckCircle size={18} color={sharedStyles.colors.success} />
                              Key Validated
                            </>
                          ) : (
                            'Validate Key'
                          )}
                        </button>

                        {/* Validation Result */}
                        {validationResult && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            borderRadius: '6px',
                            background: validationResult.valid ? sharedStyles.colors.successLight : sharedStyles.colors.errorLight,
                            border: `1px solid ${validationResult.valid ? sharedStyles.colors.success : sharedStyles.colors.error}`
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px'
                            }}>
                              {validationResult.valid ? (
                                <CheckCircle size={18} color={sharedStyles.colors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
                              ) : (
                                <XCircle size={18} color={sharedStyles.colors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
                              )}
                              <div>
                                <p style={{
                                  fontSize: '0.85rem',
                                  fontWeight: '500',
                                  color: validationResult.valid ? sharedStyles.colors.successDark : sharedStyles.colors.errorDark,
                                  margin: '0 0 4px 0'
                                }}>
                                  {validationResult.valid ? 'API Key Valid!' : 'Validation Failed'}
                                </p>
                                {validationResult.valid && validationResult.model_access && (
                                  <p style={{
                                    fontSize: '0.75rem',
                                    color: sharedStyles.colors.successDark,
                                    margin: 0
                                  }}>
                                    Model Access: {validationResult.model_access.join(', ')}
                                  </p>
                                )}
                                {!validationResult.valid && validationResult.error && (
                                  <p style={{
                                    fontSize: '0.75rem',
                                    color: sharedStyles.colors.errorDark,
                                    margin: 0
                                  }}>
                                    {validationResult.error}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Security & Cost Warnings */}
                    <div
                      style={{
                        background: `${sharedStyles.colors.primary}10`,
                        border: `1px solid ${sharedStyles.colors.primary}30`,
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '24px'
                      }}
                    >
                      <div style={{ marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span style={{ fontSize: '1rem' }}>🔒</span>
                        <p style={{
                          fontSize: '0.85rem',
                          color: sharedStyles.colors.textSecondary,
                          margin: 0,
                          lineHeight: '1.4'
                        }}>
                          Your API key is encrypted at rest and never exposed
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span style={{ fontSize: '1rem' }}>♾️</span>
                        <p style={{
                          fontSize: '0.85rem',
                          color: sharedStyles.colors.textSecondary,
                          margin: 0,
                          lineHeight: '1.4'
                        }}>
                          Unlimited reports when BYOK is enabled
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* Error Message */}
                {error && (
                  <div
                    style={{
                      background: sharedStyles.colors.errorLight,
                      border: '1px solid sharedStyles.colors.textTertiary',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}
                  >
                    <XCircle size={18} color={sharedStyles.colors.error} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <p style={{
                      fontSize: '0.85rem',
                      color: sharedStyles.colors.errorDark,
                      margin: 0
                    }}>
                      {error}
                    </p>
                  </div>
                )}

                {/* Success Message */}
                {success && (
                  <div
                    style={{
                      background: sharedStyles.colors.successLight,
                      border: `1px solid ${sharedStyles.colors.success}`,
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}
                  >
                    <CheckCircle size={18} color={sharedStyles.colors.success} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <p style={{
                      fontSize: '0.85rem',
                      color: sharedStyles.colors.successDark,
                      margin: 0
                    }}>
                      {success}
                    </p>
                  </div>
                )}

                {/* Delete Confirmation Dialog */}
                {showDeleteConfirm && (
                  <div
                    style={{
                      background: sharedStyles.colors.errorLight,
                      border: `2px solid ${sharedStyles.colors.error}`,
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '16px'
                    }}
                  >
                    <p style={{
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: sharedStyles.colors.errorDark,
                      margin: '0 0 12px 0'
                    }}>
                      Are you sure you want to delete your API key?
                    </p>
                    <p style={{
                      fontSize: '0.85rem',
                      color: sharedStyles.colors.errorDark,
                      margin: '0 0 16px 0',
                      lineHeight: '1.4'
                    }}>
                      You'll need to re-enter your key to re-enable BYOK. This action cannot be undone.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        style={{
                          ...createButton('secondary'),
                          padding: '8px 16px',
                          fontSize: '0.875rem',
                          flex: 1
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={loading}
                        style={{
                          background: sharedStyles.colors.error,
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          flex: 1,
                          opacity: loading ? 0.6 : 1
                        }}
                      >
                        {loading ? 'Deleting...' : 'Delete Key'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!loading && byokStatus.is_pro_tier && (
            <div
              style={{
                padding: '20px',
                borderTop: `1px solid ${sharedStyles.colors.surfaceAlt}`,
                display: 'flex',
                gap: '12px',
                justifyContent: 'space-between'
              }}
            >
              <div>
                {byokStatus.has_encrypted_key && !showDeleteConfirm && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={loading}
                    style={{
                      background: 'white',
                      color: sharedStyles.colors.error,
                      border: `1px solid ${sharedStyles.colors.error}`,
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    <Trash2 size={16} />
                    Delete API Key
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={onClose}
                  style={{
                    ...createButton('secondary'),
                    padding: '10px 20px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading || (showApiKeySection && apiKey.trim() && !validationResult?.valid)}
                  style={{
                    ...createButton('primary'),
                    padding: '10px 20px',
                    opacity: (loading || (showApiKeySection && apiKey.trim() && !validationResult?.valid)) ? 0.6 : 1,
                    cursor: (loading || (showApiKeySection && apiKey.trim() && !validationResult?.valid)) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>,
    document.body
  );
};

export default BYOKSettingsModal;
